import { useEffect, useRef, useState } from "react";
import {
    FileText,
    Search,
    Filter,
    ArrowUpDown,
    Calendar,
    User,
    ExternalLink,
    ChevronRight,
    LoaderCircle,
    FileBadge,
    Download,
    Upload,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";

const API = import.meta.env.VITE_API_URL || "http://localhost:9000";

interface Document {
    id: string;
    title: string;
    source?: "document" | "accounting";
    module: string;
    documentType: string;
    status: string;
    signatureStatus: string | null;
    createdAt: string;
    templateName: string | null;
    creatorName: string | null;
    entityType: string;
    entityId: string;
    signedFileKey: string | null;
    documentNumber?: string | null;
    customerName?: string | null;
}

export function DokumenterPage() {
    const [documents, setDocuments] = useState<Document[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");
    const [downloadingId, setDownloadingId] = useState<string | null>(null);
    const [uploadingId, setUploadingId] = useState<string | null>(null);
    const [uploadTarget, setUploadTarget] = useState<Document | null>(null);
    const uploadInputRef = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
        void loadDocuments();
    }, []);

    const loadDocuments = async () => {
        setIsLoading(true);
        try {
            const response = await fetch(`${API}/api/admin/documents`, { credentials: "include" });
            const data = response.ok ? await response.json() : [];
            setDocuments(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error("Failed to fetch documents:", err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleDownload = async (doc: Document) => {
        try {
            setDownloadingId(doc.id);
            const variant = doc.status === "signed" ? "signed" : "unsigned";
            const params = new URLSearchParams({ variant });
            if (doc.source === "accounting") {
                params.set("source", "accounting");
            }

            const response = await fetch(`${API}/api/admin/documents/${doc.id}/download-url?${params.toString()}`, {
                credentials: "include"
            });

            if (!response.ok) throw new Error("Kunne ikke hente nedlastingslenke");

            const { downloadUrl } = await response.json();

            // Create a temporary link and trigger download
            const link = document.createElement("a");
            link.href = downloadUrl;
            link.download = `${doc.title}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (err) {
            console.error("Nedlasting feilet:", err);
            alert("Kunne ikke laste ned dokumentet.");
        } finally {
            setDownloadingId(null);
        }
    };

    const openUploadPicker = (doc: Document) => {
        setUploadTarget(doc);
        uploadInputRef.current?.click();
    };

    const handleSignedUpload = async (file: File) => {
        if (!uploadTarget) {
            return;
        }

        try {
            setUploadingId(uploadTarget.id);
            const formData = new FormData();
            formData.append("file", file);

            const response = await fetch(`${API}/api/admin/documents/${uploadTarget.id}/manual-signed-upload`, {
                method: "POST",
                credentials: "include",
                body: formData,
            });

            if (!response.ok) {
                throw new Error("Kunne ikke laste opp signert PDF");
            }

            await loadDocuments();
            alert("Signert PDF er lastet opp. Neste steg er trigget automatisk.");
        } catch (err) {
            console.error("Opplasting feilet:", err);
            alert("Kunne ikke laste opp signert dokument.");
        } finally {
            setUploadingId(null);
            setUploadTarget(null);
            if (uploadInputRef.current) {
                uploadInputRef.current.value = "";
            }
        }
    };

    const filteredDocuments = documents.filter((doc) =>
        doc.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        doc.templateName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        doc.creatorName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        doc.documentNumber?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        doc.customerName?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const getStatusBadge = (status: string) => {
        switch (status.toLowerCase()) {
            case "signed":
                return <Badge className="bg-emerald-500/10 text-emerald-600 border-emerald-500/20">Signert</Badge>;
            case "pending_signature":
                return <Badge className="bg-amber-500/10 text-amber-700 border-amber-500/20">Venter på signering</Badge>;
            case "unsigned":
                return <Badge className="bg-slate-100 text-slate-700 border-slate-200">Klar</Badge>;
            case "draft":
                return <Badge variant="secondary" className="bg-slate-100 text-slate-600">Utkast</Badge>;
            case "pending":
                return <Badge className="bg-amber-500/10 text-amber-600 border-amber-500/20">Venter</Badge>;
            default:
                return <Badge variant="muted">{status}</Badge>;
        }
    };

    const canUploadSignedFile = (doc: Document) =>
        doc.source !== "accounting" && doc.status !== "signed" && doc.signatureStatus === "manual_pending";

    const getSignatureHint = (doc: Document) => {
        if (canUploadSignedFile(doc)) {
            return "Venter på opplasting av signert PDF";
        }

        if (doc.status === "pending_signature") {
            return "Signering pågår";
        }

        if (doc.status === "signed") {
            return "Signering fullført";
        }

        return null;
    };

    const getModuleBadge = (module: string) => {
        switch (module.toLowerCase()) {
            case "registrations":
                return <Badge variant="muted" className="border-blue-200 text-blue-700 bg-blue-50/50">Registrering</Badge>;
            case "auctions":
                return <Badge variant="muted" className="border-purple-200 text-purple-700 bg-purple-50/50">Auksjon</Badge>;
            case "accounting":
                return <Badge variant="muted" className="border-emerald-200 text-emerald-700 bg-emerald-50/50">Regnskap</Badge>;
            default:
                return <Badge variant="muted">{module}</Badge>;
        }
    };

    if (isLoading) {
        return (
            <div className="flex h-[400px] items-center justify-center">
                <LoaderCircle className="h-8 w-8 animate-spin text-primary/20" />
            </div>
        );
    }

    return (
        <div className="p-6 max-w-7xl mx-auto space-y-6">
            <input
                ref={uploadInputRef}
                type="file"
                accept="application/pdf,.pdf"
                className="hidden"
                onChange={(event) => {
                    const file = event.target.files?.[0];
                    if (file) {
                        void handleSignedUpload(file);
                    }
                }}
            />
            {/* Header */}
            <div className="flex flex-col gap-1">
                <h1 className="text-2xl font-bold tracking-tight text-slate-900">Dokumenter</h1>
                <p className="text-slate-500 text-sm">
                    Oversikt over alle dokumenter generert i systemet.
                </p>
            </div>

            {/* Stats */}
            <div className="grid gap-4 md:grid-cols-3">
                <Card className="bg-white border-slate-200/60 shadow-sm">
                    <CardContent className="p-5 flex items-center gap-4">
                        <div className="h-10 w-10 rounded-xl bg-blue-50 flex items-center justify-center text-blue-600">
                            <FileText className="h-5 w-5" />
                        </div>
                        <div>
                            <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Total dokumenter</p>
                            <p className="text-2xl font-bold text-slate-900">{documents.length}</p>
                        </div>
                    </CardContent>
                </Card>

                <Card className="bg-white border-slate-200/60 shadow-sm">
                    <CardContent className="p-5 flex items-center gap-4">
                        <div className="h-10 w-10 rounded-xl bg-emerald-50 flex items-center justify-center text-emerald-600">
                            <FileBadge className="h-5 w-5" />
                        </div>
                        <div>
                            <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Signerte dokumenter</p>
                            <p className="text-2xl font-bold text-slate-900">
                                {documents.filter(d => d.status === "signed").length}
                            </p>
                        </div>
                    </CardContent>
                </Card>

                <Card className="bg-white border-slate-200/60 shadow-sm">
                    <CardContent className="p-5 flex items-center gap-4">
                        <div className="h-10 w-10 rounded-xl bg-amber-50 flex items-center justify-center text-amber-600">
                            <Calendar className="h-5 w-5" />
                        </div>
                        <div>
                            <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Siste 30 dager</p>
                            <p className="text-2xl font-bold text-slate-900">
                                {documents.filter(d => new Date(d.createdAt) > new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)).length}
                            </p>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Table */}
            <Card className="border-slate-200/60 shadow-sm overflow-hidden">
                <div className="p-4 border-b border-slate-100 bg-slate-50/30 flex flex-col sm:flex-row gap-4 justify-between items-center">
                    <div className="relative w-full sm:w-96">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                            placeholder="Søk i dokumenter..."
                            className="pl-10 bg-white border-slate-200 focus-visible:ring-primary/20"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                    <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" className="text-slate-600 font-medium">
                            <Filter className="mr-2 h-4 w-4" />
                            Filtrer
                        </Button>
                        <Button variant="outline" size="sm" className="text-slate-600 font-medium">
                            <ArrowUpDown className="mr-2 h-4 w-4" />
                            Sorter
                        </Button>
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm">
                        <thead>
                            <tr className="bg-slate-50/50 border-b border-slate-100">
                                <th className="px-6 py-4 font-semibold text-slate-900">Tittel</th>
                                <th className="px-6 py-4 font-semibold text-slate-900">Mal</th>
                                <th className="px-6 py-4 font-semibold text-slate-900">Modul</th>
                                <th className="px-6 py-4 font-semibold text-slate-900">Status</th>
                                <th className="px-6 py-4 font-semibold text-slate-900">Opprettet av</th>
                                <th className="px-6 py-4 font-semibold text-slate-900 text-right">Dato</th>
                                <th className="px-6 py-4 font-semibold text-slate-900 text-right">Handlinger</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {filteredDocuments.length > 0 ? (
                                filteredDocuments.map((doc) => (
                                    <tr key={doc.id} className="hover:bg-slate-50/50 transition-colors group">
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3">
                                                <div className="h-8 w-8 rounded bg-slate-100 flex items-center justify-center text-slate-500 group-hover:bg-white group-hover:shadow-sm transition-all">
                                                    <FileText className="h-4 w-4" />
                                                </div>
                                                <div>
                                                    <p className="font-medium text-slate-900">{doc.title}</p>
                                                    <p className="text-[11px] text-slate-500 font-mono tracking-tighter uppercase">{doc.id.split('-')[0]}...</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className="text-slate-600">{doc.templateName || "Egendefinert"}</span>
                                        </td>
                                        <td className="px-6 py-4">
                                            {getModuleBadge(doc.module)}
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex flex-col items-start gap-1">
                                                {getStatusBadge(doc.status)}
                                                {getSignatureHint(doc) && (
                                                    <span className="text-xs text-slate-500">{getSignatureHint(doc)}</span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-slate-600">
                                            <div className="flex items-center gap-2">
                                                <div className="h-6 w-6 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center text-[10px] font-bold text-slate-600 uppercase">
                                                    {doc.creatorName?.[0] || 'S'}
                                                </div>
                                                <span>{doc.creatorName || "System"}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-right text-slate-500">
                                            <div className="flex flex-col items-end">
                                                <span>{new Date(doc.createdAt).toLocaleDateString("nb-NO")}</span>
                                                <span className="text-[11px] opacity-60">
                                                    {new Date(doc.createdAt).toLocaleTimeString("nb-NO", { hour: '2-digit', minute: '2-digit' })}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <div className="flex items-center justify-end gap-2">
                                                {canUploadSignedFile(doc) && (
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        className="gap-2 border-emerald-200 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800"
                                                        disabled={uploadingId === doc.id}
                                                        onClick={() => openUploadPicker(doc)}
                                                        title="Last opp signert PDF"
                                                    >
                                                        {uploadingId === doc.id ? (
                                                            <LoaderCircle className="h-4 w-4 animate-spin" />
                                                        ) : (
                                                            <Upload className="h-4 w-4" />
                                                        )}
                                                        Last opp signert PDF
                                                    </Button>
                                                )}
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-8 w-8 text-slate-400 hover:text-blue-600 hover:bg-blue-50"
                                                    disabled={downloadingId === doc.id}
                                                    onClick={() => handleDownload(doc)}
                                                    title="Last ned"
                                                >
                                                    {downloadingId === doc.id ? (
                                                        <LoaderCircle className="h-4 w-4 animate-spin" />
                                                    ) : (
                                                        <Download className="h-4 w-4" />
                                                    )}
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={7} className="px-6 py-12 text-center text-slate-500">
                                        {searchQuery ? "Ingen dokumenter samsvarer med søket ditt." : "Ingen dokumenter funnet."}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </Card>
        </div>
    );
}
